<template>
  <layout-with-back-btn title="Daftar Partner">
    <div class="flex items-center justify-between flex-col sm:flex-row gap-[16px] mt-[24px] mb-[16px]">
      <div class="flex items-center gap-[10px] flex-wrap">
        <b-form-select
          v-model="partnerStatus"
          :options="partnerStatusOptions"
          value-field="value"
          text-field="text"
          placeholder="Status Partner"
          class="csw sm:!w-auto m-0 !border-[1px] !border-[#e2e2e2] !rounded-lg"
        />
        <div class="d-flex rounded-lg align-items-center text-[#c2c2c2] px-[12px] py-[3px] border-[1px] border-[#e2e2e2] w-full sm:w-auto">
          <span
            class="k-search-normal-1 font-bold text-[20px] align-middle mx-[5px]"
          />
          <b-form-input
            v-model="partnerName"
            placeholder="Cari Nama Partner"
            class="sm:!w-[250px] border-0 focus:!shadow-none !rounded-r-lg"
            debounce="500"
          />
        </div>
      </div>
      <div class="flex items-center justify-end gap-[10px] flex-wrap">
        <button
          class="mr-[10px]"
          @click="getListData"
        >
          <img
            src="https://storage.googleapis.com/komerce/assets/icons/refresh-2.svg"
            alt="refresh"
            class="w-[24px] h-[24px]"
          >
        </button>
        <date-picker
          v-model="period"
          input-label-class="text-[14px] text-black"
          :date-format="partnerDateFormat"
          :ranges="partnerDateRanges"
        >
          <template #default="{ data: periodData }">
            <div class="d-flex justify-between gap-[14px] items-center w-100 p-[3px]">
              <span class="text-[14px] text-[#c2c2c2]">
                {{ getFormattedDateRange(periodData.startDate, periodData.endDate) }}
              </span>
              <span class="k-calendar text-[24px]" />
            </div>
          </template>
        </date-picker>
        <b-form-select
          v-model="partnerOrderBy"
          :options="optionsPartnerOrderBy"
          value-field="value"
          text-field="text"
          placeholder="Order By"
          class="csw sm:!w-auto m-0 !border-[1px] !border-[#e2e2e2] !rounded-lg"
        />
        <b-button
          variant="outline-primary"
          class="w-full sm:w-auto"
          :disbaled="loadingExportData"
          @click="exportData"
        >
          <b-spinner
            v-if="loadingExportData"
            small
          />
          Export Data
        </b-button>
      </div>
    </div>
    <b-table
      :items="items"
      :fields="partnerTableFields"
      empty-text="Tidak ada data yang ditampilkan."
      sticky-header="500px"
      show-empty
      responsive
      @scroll.native="handleScrollTable"
    >
      <template #cell(no)="data">
        {{ data.index + 1 }}
      </template>
      <template #cell(contact)="data">
        <div v-if="data.value">
          <span class="inline-block left mr-[5px]">{{ data.value }}</span>
          <button
            class="inline-block left text-[16px]"
            @click="copyContactNumber(data.value)"
          >
            <span class="k-copy" />
          </button>
        </div>
        <a
          v-if="data.item.email"
          :href="`mailto:${data.item.email}`"
          target="_blank"
          class="block left text-[#08A0F7]"
        >
          {{ data.item.email }}
        </a>
      </template>
      <template #cell(warehouses)="data">
        <ul
          v-if="data.value"
          class="list-disc ml-[16px]"
        >
          <li
            v-for="(warehouse, index) in data.value"
            :key="`${warehouse} ${index}`"
          >
            {{ warehouse }}
          </li>
        </ul>
        <span v-else>-</span>
      </template>
      <template #cell(status)="data">
        <span
          class="rounded-full px-[8px] py-[2px] text-center"
          :class="formatStatusPartner(data.item.status).css"
        >
          {{ formatStatusPartner(data.item.status).text }}
        </span>
      </template>
      <template #cell(partner_id)="data">
        <b-button
          variant="outline-primary"
          size="sm"
          @click="viewPartnerDetail(data.item.partner_id, data.item.name)"
        >
          Lihat Detail
        </b-button>
      </template>
    </b-table>
    <div
      v-if="loading"
      class="text-center -mt-[34px] bg-white z-[10] relative pt-[16px]"
    >
      <b-spinner
        class="align-middle mr-[4px]"
        small
        variant="primary"
      />
      <span>memuat data...</span>
    </div>
    <modal-detail ref="modalDetail" />
  </layout-with-back-btn>
</template>

<script>
import { firstDateOfMonth, last2 } from '@/store/helpers'
import { DAY_MONTH_YEAR, YEAR_MONTH_DAY, createDatePickerRestrictor } from '@/libs/filterDate'
import { handleInfiniteScrollBTable } from '@/libs/scrollUtils'
import { downloadFileFromBlob } from '@/libs/downloadFile'
import DatePicker from '../DatePicker.vue'
import { fields, partnerStatusOptions, optionsPartnerOrderBy } from './config'
import { datePickerRangeGeneral } from '../config'
import { getFormattedDateRange } from '../helpers'
import LayoutWithBackBtn from '../LayoutWithBackBtn.vue'
import ModalDetail from './components/ModalDetail.vue'

/**
 * @component PartnerList
 * @description A component that displays a list of partners
 */
export default {
  components: {
    LayoutWithBackBtn,
    DatePicker,
    ModalDetail,
  },
  data() {
    return {
      partnerStatus: '0',
      partnerOrderBy: 'all',
      isTabsLoading: true,
      loadingExportData: false,
      partnerName: '',
      limit: 30,
      period: {
        startDate: this.$route.query.start_date ? new Date(this.$route.query.start_date) : firstDateOfMonth,
        endDate: this.$route.query.end_date ? new Date(this.$route.query.end_date) : last2,
      },
      items: [],
      loading: false,
      offset: 0,
      lastData: false,
    }
  },
  computed: {
    totalRows() {
      return this.items.length
    },
  },
  watch: {
    partnerStatus() {
      this.getListData()
    },
    partnerOrderBy() {
      this.getListData()
    },
    partnerName() {
      this.getListData()
    },
    period() {
      this.getListData()
    },
  },
  created() {
    this.partnerStatusOptions = partnerStatusOptions
    this.optionsPartnerOrderBy = optionsPartnerOrderBy
    this.partnerDateFormat = createDatePickerRestrictor((currentDate, endDate) => currentDate >= endDate)
    this.partnerDateRanges = datePickerRangeGeneral(last2)
    this.partnerTableFields = fields
  },
  mounted() {
    this.getListData()
  },
  methods: {
    getFormattedDateRange,
    DAY_MONTH_YEAR,
    createDatePickerRestrictor,
    /**
     * Copies the partner's contact number to clipboard
     * @param {string} value - The contact number to copy
     */
    copyContactNumber(value) {
      this.$toast_success({ message: 'Berhasil menyalin nomor kontak.' })
      navigator.clipboard.writeText(value)
    },
    /**
     * Formats the partner status so they be colorful and descriptive
     * @param {number} status - The partner status
     * @returns {Object} - The formatted status
     */
    formatStatusPartner(status) {
      if (status === 2) {
        return {
          text: 'Aktif',
          css: 'bg-[#e6f6ea] text-[#15803d]',
        }
      }
      if (status === 3) {
        return {
          text: 'Pasif',
          css: 'bg-[#f3f4f6] text-[#374151]',
        }
      }
      if (status === 4) {
        return {
          text: 'In-Active',
          css: 'text-[#E31A1A] bg-[#FFEDED]',
        }
      }
      return {
        text: 'Partner Baru',
        css: 'text-[#4285F4] bg-[#DFF3FF]',
      }
    },
    /**
     * Handles the scroll event on the table to implement infinite scrolling
     * @param {Event} event - The scroll event
     */
    handleScrollTable(event) {
      handleInfiniteScrollBTable(event, !this.loading && !this.lastData, () => this.fetchData(false))
    },
    /**
     * Fetches partner data from the API
     * @param {boolean} isNewSearch - Whether this is a new search or loading more data
     * @returns {Promise<void>}
     */
    async fetchData(isNewSearch = true) {
      if (this.loading) return

      this.loading = true
      if (isNewSearch) {
        this.offset = 0
        this.lastData = false
      }

      const url = '/kompack/api/v1/dashboard/admin/partners'

      try {
        const response = await this.$http_new.get(url, {
          params: {
            offset: this.offset,
            limit: this.limit,
            status: this.partnerStatus,
            order_by: this.partnerOrderBy,
            search: this.partnerName,
            start_date: YEAR_MONTH_DAY(this.period.startDate),
            end_date: YEAR_MONTH_DAY(this.period.endDate),
          },
        })
        const { data } = response.data

        if (!data) {
          this.lastData = true
          if (isNewSearch) this.items = []
          return
        }

        if (isNewSearch) {
          this.items = data
        } else {
          this.items.push(...data)
        }

        this.offset += data.length
      } catch (_err) {
        this.$toast_error({ message: 'Gagal mengambil data daftar partner, silahkan coba lagi.' })
        this.lastData = true
      } finally {
        this.loading = false
        if (this.isTabsLoading) {
          this.isTabsLoading = false
        }
      }
    },
    /**
     * Resets and fetches the partner list data
     */
    getListData() {
      this.fetchData(true)
    },
    /**
     * Exports partner data to an Excel file
     */
    exportData() {
      this.loadingExportData = true
      this.$http_new.post('/kompack/api/v1/dashboard/admin/partners/export-data', null, {
        params: {
          status: this.partnerStatus,
          search: this.partnerName,
          order_by: this.order_by,
          start_date: YEAR_MONTH_DAY(this.period.startDate),
          end_date: YEAR_MONTH_DAY(this.period.endDate),
        },
        responseType: 'blob',
      }).then(res => {
        const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        downloadFileFromBlob(blob, `daftar_partner_${DAY_MONTH_YEAR(this.period.startDate)} - ${DAY_MONTH_YEAR(this.period.endDate)}.xlsx`)
      }).catch(_err => {
        this.$toast_error({ message: 'Gagal mengunduh file, silahkan coba lagi.' })
      }).finally(() => {
        this.loadingExportData = false
      })
    },
    viewPartnerDetail(id, partnerName) {
      this.$refs.modalDetail.show(id, partnerName)
    },
  },
}
</script>

<style scoped>
.csw {
  background-image: url('https://storage.googleapis.com/komerce/assets/icons/sort-icon-new.svg');
  background-size: 13px;
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 50px;
}
</style>
